import React, { useState } from "react";
import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { uploadAuthDocument, updateCompanyDocumentSuperAdmin } from "../../../Services/ApiCalling/document";
import { fetchCompanyById } from "../../../Redux/Slices/gnaAdminSlice";
import Styles from "./CompanyDetails.module.css";

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB Limit

const verifyPdfHeader = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = (e) => {
      if (!e.target || e.target.readyState !== FileReader.DONE) {
        resolve(false);
        return;
      }
      const arr = new Uint8Array(e.target.result).subarray(0, 4);
      let header = "";
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16);
      }
      resolve(header.toLowerCase() === "25504446");
    };
    reader.readAsArrayBuffer(file.slice(0, 4));
  });
};

const DocumentUploaderModal = ({ open, onClose, companyId, documentKey, documentLabel, isReplacement, traderEntityMapId }) => {
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploadedDocId, setUploadedDocId] = useState(null);
  const [fileName, setFileName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [expiryAt, setExpiryAt] = useState("");

  const isBusy = uploading || saving;

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setErrorMessage("");

    // Helper to reset file input value on validation failure
    const resetInput = () => {
      if (e.target) e.target.value = "";
    };

    // 1. File size check
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const msg = "File size exceeds the 10 MB limit.";
      setErrorMessage(msg);
      toast.error(msg);
      resetInput();
      return;
    }

    // 2. MIME & extension check
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      const msg = "Only PDF files are allowed.";
      setErrorMessage(msg);
      toast.error(msg);
      resetInput();
      return;
    }

    // 3. Binary Magic Byte Validation (%PDF header check)
    const isRealPdf = await verifyPdfHeader(file);
    if (!isRealPdf) {
      const msg = "Invalid file format: Content does not match a valid PDF signature.";
      setErrorMessage(msg);
      toast.error(msg);
      resetInput();
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const res = await uploadAuthDocument(file, (progressEvent) => {
        if (progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      if (res?.data?.code === 200) {
        setUploadedDocId(res?.data?.data?.id);
        setFileName(file.name);
        toast.success("File uploaded. Click Save to confirm.");
      } else {
        const errorText = res?.data?.error || res?.data?.message || "File upload failed.";
        setErrorMessage(errorText);
        toast.error(errorText);
        resetInput();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Network error uploading file.";
      setErrorMessage(msg);
      toast.error(msg);
      resetInput();
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSave = async () => {
    if (!uploadedDocId) {
      setErrorMessage("Please upload a valid PDF first.");
      return;
    }
    setErrorMessage("");
    try {
      setSaving(true);
    
      const payload = {
        companyId,
        documentKey,
        docId: uploadedDocId,
      };
      if (documentKey === "nocDoc") {
        if (!expiryAt) {
          setErrorMessage("Please select the NOC expiry date.");
          setSaving(false);
          return;
        }
        payload.expiryAt = expiryAt;
        payload.traderEntityMapId = traderEntityMapId;
      }
      const res = await updateCompanyDocumentSuperAdmin(payload);

      if (res?.data?.code === 200) {
        toast.success(`Document ${isReplacement ? "replaced" : "uploaded"} successfully!`);
        dispatch(fetchCompanyById(companyId)); // Standard refresh of company state
        handleClose();
      } else {
        const errorText = res?.data?.error || res?.data?.message || "Failed to update document mapping.";
        setErrorMessage(errorText);
        logTelemetryError("DOCUMENT_MAPPING_SAVE_FAILED", { companyId, documentKey, uploadedDocId, error: errorText });
        toast.error(errorText);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Server error while updating mapping. Click Retry to try again.";
      setErrorMessage(msg);
      logTelemetryError("DOCUMENT_MAPPING_SAVE_EXCEPTION", { companyId, documentKey, uploadedDocId, error: err.message });
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const logTelemetryError = (eventName, payload) => {
    console.warn(`[Telemetry Log: ${eventName}]`, payload);
    if (window.Sentry) {
      window.Sentry.captureMessage(eventName, { extra: payload });
    }
  };

  const handleClose = () => {
    if (isBusy) return;
    if (uploadedDocId) {
      console.warn(`[Orphaned File Notice] User closed modal with unlinked docId: ${uploadedDocId}. Left for 24h backend GC.`);
    }
    setUploadedDocId(null);
    setFileName("");
    setErrorMessage("");
    setUploadProgress(0);
    setExpiryAt("");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (isBusy) return;
        handleClose();
      }}
    >
      <div className={Styles.editDocModal}>
        <div className={Styles.editDocModalHeader}>
          <span className={Styles.editDocModalTitle}>
            {isReplacement ? "Replace" : "Upload"} Document — {documentLabel}
          </span>
          <button className={Styles.cfgModalClose} onClick={handleClose} disabled={isBusy}>
            <CloseIcon style={{ fontSize: "16px" }} />
          </button>
        </div>

        <div className={Styles.editDocModalBody}>
          <p className={Styles.editDocModalHint}>
            {isReplacement
              ? `Select a new PDF file (max 10 MB) to replace ${documentLabel}.`
              : `Select a PDF file (max 10 MB) to upload for ${documentLabel}.`}
          </p>

                    {errorMessage && (
            <div className={Styles.inlineErrorBanner}>
              <ErrorOutlineIcon style={{ fontSize: "16px" }} />
              <span>{errorMessage}</span>
            </div>
          )}

          {documentKey === "nocDoc" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "16px" }}>
              <label style={{ fontSize: "13px", fontWeight: 500, color: "#333" }}>NOC Expiry Date *</label>
              <input
                type="date"
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "14px" }}
                value={expiryAt}
                onChange={(e) => setExpiryAt(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                disabled={isBusy}
              />
            </div>
          )}


          <label
            className={`${Styles.editDocUploadArea} ${isBusy ? Styles.disabledUploadArea : ""}`}
            htmlFor={isBusy ? undefined : "editDocInput"}
          >
            {uploading ? (
              <div className={Styles.statusRow}>
                <span className={Styles.editDocSpinner} />
                <span>Uploading PDF... {uploadProgress}%</span>
              </div>
            ) : fileName ? (
              <span className={Styles.editDocFileName}>✓ {fileName}</span>
            ) : (
              <span className={Styles.editDocPlaceholder}>Click to select PDF file (max 10 MB)</span>
            )}
          </label>
          <input
            id="editDocInput"
            type="file"
            accept=".pdf,application/pdf"
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={isBusy}
          />
        </div>

        <div className={Styles.editDocModalFooter}>
          <button className={Styles.cfgCancelBtn} onClick={handleClose} disabled={isBusy}>
            Cancel
          </button>
          <button
            className={Styles.cfgSubmitBtn}
            onClick={handleSave}
            disabled={!uploadedDocId || isBusy}
          >
            {saving ? "Saving..." : errorMessage && uploadedDocId ? "Retry Save" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentUploaderModal;
